// React Query hooks for the Finance Tab: the derived summary, purchase-order
// CRUD, and the invoice + finance-setup surfaces (list/item/create/update/
// delete/approve/query, plus the setup singleton). Mirrors useTracking.ts
// conventions — positional-array query keys, the shared `trackingRetry`
// retry policy, and invalidate-on-mutation-success.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { financeApi } from "../services/api";
import { trackingRetry } from "./trackingRetry";
import { trackingKeys } from "./trackingKeys";
import type {
  FinanceSummary,
  PurchaseOrderListResponse,
  PurchaseOrderWithDerived,
  PurchaseOrder,
  CreatePurchaseOrderInput,
  UpdatePurchaseOrderInput,
  InvoiceListResponse,
  InvoiceWithDerived,
  Invoice,
  InvoiceStatus,
  CreateInvoiceInput,
  UpdateInvoiceInput,
  ApproveInvoiceInput,
  ApproveInvoiceResponse,
  SetupResponse,
  FinanceSetupPutInput,
} from "../types/finance";

// ─── Invalidation helper ─────────────────────────────────────────────────
// Mirrors invalidateForContractType in useTracking.ts. `["finance",
// contractId]` is a prefix match so it refreshes the summary
// (`["finance", contractId, "summary"]`), the purchase-order list/item
// caches (`["finance", contractId, "purchase-orders", ...]`), the invoice
// list/item caches (`["finance", contractId, "invoices", ...]`), and the
// setup cache (`["finance", contractId, "setup"]`) in one call. Every
// mutation in this file — PO or invoice or setup — shifts at least one of
// those derived views, so the broad prefix is the correct scope throughout.
// The activity feed is invalidated too — PO/invoice creates/edits/deletes/
// status-changes surface there.
function invalidateFinance(
  queryClient: ReturnType<typeof useQueryClient>,
  contractId: string,
): void {
  queryClient.invalidateQueries({ queryKey: ["finance", contractId] });
  queryClient.invalidateQueries({ queryKey: ["activity", contractId] });
  // The Finance tab badge (invoices awaiting review) reads the tracking
  // counts query; invoice mutations move invoices in/out of the review
  // queue, so the badge must refetch rather than sit stale for its
  // 30s staleTime.
  queryClient.invalidateQueries({ queryKey: trackingKeys.counts(contractId) });
}

// ─── Queries ─────────────────────────────────────────────────────────────

export function useFinanceSummary(contractId: string) {
  return useQuery<FinanceSummary>({
    queryKey: ["finance", contractId, "summary"],
    queryFn: () => financeApi.getSummary(contractId),
    enabled: !!contractId,
    retry: trackingRetry,
  });
}

export function usePurchaseOrders(contractId: string) {
  return useQuery<PurchaseOrderListResponse>({
    queryKey: ["finance", contractId, "purchase-orders"],
    queryFn: () => financeApi.listPurchaseOrders(contractId),
    enabled: !!contractId,
    retry: trackingRetry,
  });
}

export function usePurchaseOrder(contractId: string, poId: string) {
  return useQuery<PurchaseOrderWithDerived>({
    queryKey: ["finance", contractId, "purchase-orders", poId],
    queryFn: () => financeApi.getPurchaseOrder(contractId, poId),
    enabled: !!contractId && !!poId,
    retry: trackingRetry,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────

interface CreatePurchaseOrderVariables {
  contractId: string;
  input: CreatePurchaseOrderInput;
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, input }: CreatePurchaseOrderVariables) =>
      financeApi.createPurchaseOrder(contractId, input),
    onSuccess: (_data: PurchaseOrder, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}

interface UpdatePurchaseOrderVariables {
  contractId: string;
  poId: string;
  input: UpdatePurchaseOrderInput;
}

export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, poId, input }: UpdatePurchaseOrderVariables) =>
      financeApi.updatePurchaseOrder(contractId, poId, input),
    onSuccess: (_data: PurchaseOrder, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}

interface DeletePurchaseOrderVariables {
  contractId: string;
  poId: string;
}

export function useDeletePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, poId }: DeletePurchaseOrderVariables) =>
      financeApi.deletePurchaseOrder(contractId, poId),
    onSuccess: (_data: void, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}

// ─── Invoice queries ─────────────────────────────────────────────────────

export function useInvoices(
  contractId: string,
  params?: { status?: InvoiceStatus },
) {
  return useQuery<InvoiceListResponse>({
    queryKey: ["finance", contractId, "invoices", params?.status ?? "all"],
    queryFn: () => financeApi.listInvoices(contractId, params),
    enabled: !!contractId,
    retry: trackingRetry,
  });
}

export function useInvoice(contractId: string, invoiceId: string) {
  return useQuery<InvoiceWithDerived>({
    queryKey: ["finance", contractId, "invoices", "item", invoiceId],
    queryFn: () => financeApi.getInvoice(contractId, invoiceId),
    enabled: !!contractId && !!invoiceId,
    retry: trackingRetry,
  });
}

export function useFinanceSetup(contractId: string) {
  return useQuery<SetupResponse>({
    queryKey: ["finance", contractId, "setup"],
    queryFn: () => financeApi.getSetup(contractId),
    enabled: !!contractId,
    retry: trackingRetry,
  });
}

// ─── Invoice mutations ───────────────────────────────────────────────────
// Every mutation below shifts derived balances/positions (approve/query
// change PO consumption; create/update/delete change the invoice set; a
// setup change re-derives payables), so the broad `invalidateFinance` prefix
// invalidation — summary + PO list/items + invoice list/items + setup — is
// the correct scope for all of them, same as the PO mutations above.

interface CreateInvoiceVariables {
  contractId: string;
  input: CreateInvoiceInput;
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, input }: CreateInvoiceVariables) =>
      financeApi.createInvoice(contractId, input),
    onSuccess: (_data: Invoice, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}

interface UpdateInvoiceVariables {
  contractId: string;
  invoiceId: string;
  input: UpdateInvoiceInput;
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, invoiceId, input }: UpdateInvoiceVariables) =>
      financeApi.updateInvoice(contractId, invoiceId, input),
    onSuccess: (_data: Invoice, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}

interface DeleteInvoiceVariables {
  contractId: string;
  invoiceId: string;
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, invoiceId }: DeleteInvoiceVariables) =>
      financeApi.deleteInvoice(contractId, invoiceId),
    onSuccess: (_data: void, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}

interface ApproveInvoiceVariables {
  contractId: string;
  invoiceId: string;
  input?: ApproveInvoiceInput;
}

export function useApproveInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, invoiceId, input }: ApproveInvoiceVariables) =>
      financeApi.approveInvoice(contractId, invoiceId, input),
    onSuccess: (_data: ApproveInvoiceResponse, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}

interface QueryInvoiceVariables {
  contractId: string;
  invoiceId: string;
}

export function useQueryInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, invoiceId }: QueryInvoiceVariables) =>
      financeApi.queryInvoice(contractId, invoiceId),
    onSuccess: (_data: Invoice, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}

// ─── Revert invoice mutation ──────────────────────────────────────────────

interface RevertInvoiceVariables {
  contractId: string;
  invoiceId: string;
  reason?: string;
}

export function useRevertInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, invoiceId, reason }: RevertInvoiceVariables) =>
      financeApi.revertInvoice(contractId, invoiceId, reason),
    onSuccess: (_data: Invoice, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}

// ─── Finance setup mutation ──────────────────────────────────────────────

interface PutFinanceSetupVariables {
  contractId: string;
  input: FinanceSetupPutInput;
}

export function usePutFinanceSetup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, input }: PutFinanceSetupVariables) =>
      financeApi.putSetup(contractId, input),
    onSuccess: (_data: SetupResponse, vars) => {
      invalidateFinance(queryClient, vars.contractId);
    },
  });
}
